/* Kaily OS — Director Training (Phase 6).
 *
 * Strict 5-tab layout for director skill-building. Sales / CRM / Cafe
 * pipeline / Revenue targets / DM drafts live in Studio only. OS never
 * fires LLM calls on page load or tab switch; all AI usage is manual.
 *
 * Tabs: today · references · creative-lab · works · growth
 * Back-to-Studio link preserved for sales/clients/cafes legacy workflows.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Sun, Compass, Loader2, LogOut, Clapperboard, BookMarked, Target as TargetIcon, Camera, ExternalLink,
} from "lucide-react";
import { loginStudio, clearStudioPassword, isStudioAuthed } from "@/lib/studioAuth";
import { osGet, t } from "./os/osApi";
import OSPracticeToday from "./os/OSPracticeToday";
import OSDirector from "./os/OSDirector";
import OSStudio from "./os/OSStudio";
import OSProgress from "./os/OSProgress";
import OSDirectorTraining from "./os/OSDirectorTraining";

const TABS = [
    { id: "today",        icon: Sun,          en: "Today's practice",   cn: "今日练习" },
    { id: "references",   icon: BookMarked,   en: "References",         cn: "参考库" },
    { id: "creative-lab", icon: Clapperboard, en: "Creative Lab",       cn: "创意实验室" },
    { id: "works",        icon: Camera,       en: "Practice works",     cn: "练习作品" },
    { id: "growth",       icon: Compass,      en: "Retrospective & Growth", cn: "复盘与成长" },
];

function Gate({ onOk, lang }) {
    const [pwd, setPwd] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true); setErr("");
        try {
            await loginStudio(pwd);
            await osGet("/os/progress");
            onOk();
        } catch {
            clearStudioPassword();
            setErr(t(lang, "Wrong password", "密码错误"));
        } finally { setBusy(false); }
    };
    return (
        <div className="min-h-screen bg-[#0D0A08] flex items-center justify-center px-6" style={{ colorScheme: "dark" }}>
            <div className="w-full max-w-sm text-center" data-testid="os-gate">
                <p className="font-serif text-4xl text-[#F5F0E8]">Kaily <span className="text-[#D4A574]">OS</span></p>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#F5F0E8]/40 mt-2">Director Training</p>
                <input
                    type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && pwd && submit()}
                    placeholder={t(lang, "Studio password", "Studio 密码")}
                    data-testid="os-gate-password"
                    className="mt-8 w-full rounded-full bg-white/5 border border-white/15 text-[#F5F0E8] px-5 py-3.5 text-sm text-center focus:outline-none focus:border-[#D4A574]"
                />
                {err && <p className="text-[#C0604A] text-[12px] mt-2">{err}</p>}
                <button onClick={submit} disabled={busy || !pwd} data-testid="os-gate-submit"
                    className="mt-4 w-full rounded-full bg-[#D4A574] text-[#0D0A08] py-3.5 text-[12px] uppercase tracking-[0.2em] font-bold hover:bg-[#F5F0E8] transition-colors disabled:opacity-40">
                    {busy ? <Loader2 size={15} className="animate-spin mx-auto" /> : t(lang, "Enter", "进入")}
                </button>
            </div>
        </div>
    );
}

export default function OS() {
    const [authed, setAuthed] = useState(false);
    const [checked, setChecked] = useState(false);
    const [lang, setLang] = useState(localStorage.getItem("os-lang") || "cn");
    const [tab, setTab] = useState("today");

    useEffect(() => {
        (async () => {
            if (isStudioAuthed()) {
                try { await osGet("/os/progress"); setAuthed(true); } catch { /* gate */ }
            }
            setChecked(true);
        })();
    }, []);

    const switchLang = () => {
        const next = lang === "cn" ? "en" : "cn";
        setLang(next);
        localStorage.setItem("os-lang", next);
    };

    if (!checked) return <div className="min-h-screen bg-[#0D0A08]" />;
    if (!authed) return <Gate lang={lang} onOk={() => setAuthed(true)} />;

    return (
        <div className="min-h-screen bg-[#0D0A08] text-[#F5F0E8]" style={{ colorScheme: "dark" }} data-testid="os-shell">
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0D0A08]/85 border-b border-white/8">
                <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center justify-between">
                    <div>
                        <p className="font-serif text-xl leading-none">Kaily <span className="text-[#D4A574]">OS</span></p>
                        <p className="text-[8.5px] uppercase tracking-[0.28em] text-[#F5F0E8]/35 mt-0.5">Director Training</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/studio" data-testid="os-return-studio"
                            className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#F5F0E8]/70 hover:border-[#D4A574] hover:text-[#D4A574] transition-colors">
                            <ExternalLink size={11} /> {t(lang, "Studio (Sales & Clients)", "Studio (销售 / 客户)")}
                        </Link>
                        <button onClick={switchLang} data-testid="os-lang-toggle"
                            className="rounded-full border border-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.14em] hover:border-[#D4A574] transition-colors">
                            {lang === "cn" ? "EN" : "中文"}
                        </button>
                        <button onClick={() => { clearStudioPassword(); setAuthed(false); }} className="text-[#F5F0E8]/40 hover:text-[#F5F0E8]" data-testid="os-logout">
                            <LogOut size={15} />
                        </button>
                    </div>
                </div>
                <div className="max-w-6xl mx-auto px-5 flex gap-1 overflow-x-auto">
                    {TABS.map((tb) => (
                        <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`os-tab-${tb.id}`}
                            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[12px] uppercase tracking-[0.14em] font-bold border-b-2 transition-colors whitespace-nowrap ${tab === tb.id ? "border-[#D4A574] text-[#D4A574]" : "border-transparent text-[#F5F0E8]/45 hover:text-[#F5F0E8]"}`}>
                            <tb.icon size={14} /> {t(lang, tb.en, tb.cn)}
                        </button>
                    ))}
                </div>
                {/* Mobile Studio link below tabs */}
                <div className="sm:hidden max-w-6xl mx-auto px-5 pb-2">
                    <Link to="/studio" data-testid="os-return-studio-mobile"
                        className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#F5F0E8]/55 hover:text-[#D4A574]">
                        <ExternalLink size={11} /> {t(lang, "Sales & Clients → Studio", "销售与客户 → Studio")}
                    </Link>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 py-8">
                {tab === "today" && <OSDirectorTraining lang={lang} only="abilities" />}
                {tab === "references" && <OSDirectorTraining lang={lang} only="references" />}
                {tab === "creative-lab" && <OSDirectorTraining lang={lang} only="footage" />}
                {tab === "works" && <OSDirectorTraining lang={lang} only="versions" />}
                {tab === "growth" && <GrowthWithRetro lang={lang} />}
            </main>
        </div>
    );
}

function GrowthWithRetro({ lang }) {
    return (
        <div className="space-y-14">
            <OSStudio lang={lang} only="retro" />
            <OSProgress lang={lang} />
        </div>
    );
}
